import store from '../state/_store.js'
import { deleteQueryParam, getQueryParam, pushQueryParam } from '../helpers/_queryParams.js'
import { showLoader, getResults, sortPageIds } from '../helpers/_utils.js'

const mergeFilters = (newFilters) => {
  const getFilters = ['filterText', 'selectSort', 'pressYear', 'pressCat']

  const url = new URL(window.location)
  const searchParams = url.searchParams

  for (const [key, _] of searchParams.entries()) {
    if (!getFilters.includes(key) && key !== 'q') {
      deleteQueryParam(key)
    }
  }

  const defaultFilters = {}
  for (const key of getFilters) {
    defaultFilters[key] = getQueryParam(key)
  }

  const filtered = Object.fromEntries(
    Object.entries({ ...defaultFilters, ...newFilters }).filter(([_, v]) => v != null),
  )
  return filtered
}

export const handleQuery = async (query, newFilters = {}) => {
  const queryString = query.toLowerCase() || ''
  const filters = mergeFilters(newFilters)

  pushQueryParam('q', query.toLowerCase())

  const requestBody = {
    q: queryString,
    nonce: store.search.nonce,
  }
  for (const filter in filters) {
    requestBody[filter] = filters[filter]
  }
  if (store.search.hp) {
    requestBody.hpkey = store.search.hp
  }

  const results = await getResults(requestBody, store.search.token)

  // Store initial call for use everywhere else
  // No content is fetched at this point as we'll do that later during render and again when the page or sort is changed
  store.updateMeta('currentPage', 1)
  store.updateMeta('totalItems', results?.totalNumberOfResults)
  store.addItems(results.items)

  // Doesn't seem like this is part of the responsibility of this function
  // Consider looking for a better home
  buildPagination(1)
  return store.dispatch('displayResults', [results.items])
}

export const updateSort = async (sort) => {
  let currentItems = store.getItems()

  if (currentItems.length == 0) {
    let query = getQueryParam('q')
    const filters = mergeFilters()

    const params = new URLSearchParams(`?q=${query.toLowerCase()}`)
    for (const filter in filters) {
      params.append(filter, filters[filter])
      pushQueryParam(filter, filters[filter])
    }

    const results = await getResults(params.toString(), store.search.token)
    store.updateMeta('totalItems', results?.totalNumberOfResults)
    currentItems = results.items
  }

  const sortMap = {
    relevance: 'score',
    datedescending: 'dateTime',
    dateascending: 'dateTime',
  }

  let sortedItems = sortPageIds([...currentItems], sortMap[sort])
  if (sort === 'datedescending') {
    sortedItems = [...sortedItems].reverse()
  }

  store.updateMeta('currentPage', 1)
  store.addItems(sortedItems)

  return store.dispatch('displayResults', [sortedItems])
}

export const showPagination = () => {
  const totalPages = store.totalPages()
  const paginationContainer = document.querySelector('.pagination')
  if (totalPages > 1) {
    paginationContainer.classList.remove('pagination--hide')
  } else {
    paginationContainer.classList.add('pagination--hide')
  }
}

export const renderMeta = () => {
  const resultsMeta = document.querySelector('.query__meta')

  const total = store.meta('totalItems')
  const perPage = store.meta('perPage')
  const page = store.meta('currentPage') || 1

  const firstItem = (page - 1) * perPage + 1
  let lastItem = page * perPage

  if (lastItem > total) lastItem = total
  resultsMeta.innerHTML = `Showing ${firstItem}-${lastItem} of ${total} results`
}

// build pagination
const buildPagination = (currentPage) => {
  const totalPages = store.totalPages()
  if (totalPages <= 1) {
    return
  }

  const paginationMeta = document.querySelector('.pagination__label')

  paginationMeta.innerText = `Page ${currentPage} of ${totalPages}`
  const paginationWrapper = document.querySelector('.pagination__list')

  while (paginationWrapper.firstChild) {
    paginationWrapper.removeChild(paginationWrapper.lastChild)
  }

  // const paginationItems = []
  let currentPageInt = parseInt(currentPage)
  let current = currentPageInt,
    last = totalPages,
    delta = 5,
    left = current - delta,
    right = current + delta - 1,
    range = [],
    rangeWithDots = [],
    l

  for (let i = 1; i <= last; i++) {
    if (i == last || (i >= left && i < right)) {
      range.push(i)
    }
  }

  for (let i of range) {
    if (l) {
      if (i - l === 2) {
        rangeWithDots.push(l + 1)
      } else if (i - l !== 1) {
        rangeWithDots.push('...')
      }
    }
    rangeWithDots.push(i)
    l = i
  }

  const prevPaginationItem = document.createElement('li')
  prevPaginationItem.classList.add('pagination__item', 'pagination__item--prev')
  if (currentPage == 1) {
    prevPaginationItem.classList.add('collapse')
  }
  prevPaginationItem.innerHTML = `<span class="pagination__link arrow_left"></span>`

  paginationWrapper.appendChild(prevPaginationItem)

  if (left > 1) {
    const createdPaginationItem = document.createElement('li')
    createdPaginationItem.classList.add('pagination__item')
    createdPaginationItem.innerHTML = `<a href="#" class="pagination__link">1</a>`
    paginationWrapper.appendChild(createdPaginationItem)

    const createdPaginationItemSpacer = document.createElement('li')
    createdPaginationItemSpacer.classList.add('pagination__item')
    createdPaginationItemSpacer.classList.add('pagination__item--ellipsis')
    //because JS can convert a string to an int, but can't handle a space in a string
    createdPaginationItemSpacer.innerHTML = `<span class="pagination__link">...</span>`
    paginationWrapper.appendChild(createdPaginationItemSpacer)
  }

  rangeWithDots.forEach((item) => {
    const createdPaginationItem = document.createElement('li')
    createdPaginationItem.classList.add('pagination__item')
    let innerHTML = `<a href="#" class="pagination__link">${item}</a>`
    if (item === currentPageInt) {
      createdPaginationItem.classList.add('pagination__item--active')
    } else if (item === '...') {
      createdPaginationItem.classList.add('pagination__item--ellipsis')
      innerHTML = `<span class="pagination__link">${item}</span>`
    }

    createdPaginationItem.innerHTML = innerHTML

    paginationWrapper.appendChild(createdPaginationItem)
  })

  const nextPaginationItem = document.createElement('li')
  nextPaginationItem.classList.add('pagination__item', 'pagination__item--next')
  if (currentPage == totalPages) {
    nextPaginationItem.classList.add('collapse')
  }
  nextPaginationItem.innerHTML = `<span class="pagination__link arrow_right"></span>`

  paginationWrapper.appendChild(nextPaginationItem)

  initPaginationHandlers()
}

// function on click go to page and hide all items not on current page and add active class to current page
const goToPage = async (page) => {
  if (typeof page === 'object') {
    return
  }

  const parsedPage = parseInt(page)

  if (window.Object.is(parsedPage, NaN)) {
    return
  }

  const main = document.querySelector('.search__main')
  showLoader(main)

  let items = store.getItems()

  if (items.length == 0) {
    await handleQuery(getQueryParam('q'))
  }

  pushQueryParam('page', parsedPage)
  store.updateMeta('currentPage', parsedPage)

  // ! Subscibe/dispatch pagination container
  buildPagination(parsedPage)
  checkIfDisabled()
  renderMeta()

  // ! Should I be sending the sliced list here or let it happen later like it is currently
  return store.dispatch('displayResults', [store.getItems()])
}

// check if pagination prev and next buttons are disabled
const checkIfDisabled = () => {
  const currentPage = store.meta('currentPage')
  const totalPages = store.totalPages()

  let paginationPrevButton = document.querySelector('.pagination__item--prev')
  let paginationNextButton = document.querySelector('.pagination__item--next')

  if (currentPage == 1) {
    paginationPrevButton.classList.add('collapse')
    paginationNextButton.classList.remove('collapse')
  } else if (currentPage == totalPages) {
    paginationPrevButton.classList.remove('collapse')
    paginationNextButton.classList.add('collapse')
  } else {
    paginationPrevButton.classList.remove('collapse')
    paginationNextButton.classList.remove('collapse')
  }
}
// add event listener to pagination items
const initPaginationHandlers = () => {
  const searchTop = document.querySelector('form.search')
  const paginationNavItems = document.querySelectorAll('li.pagination__item')

  paginationNavItems.forEach((pageItem) => {
    if (
      !pageItem.classList.contains('pagination__item--prev') &&
      !pageItem.classList.contains('pagination__item--next') &&
      !pageItem.classList.contains('pagination__item--ellipsis')
    ) {
      const link = pageItem.querySelector('a.pagination__link')
      if (link) {
        link.addEventListener('click', (event) => {
          event.preventDefault()
          const page = event.currentTarget.innerText

          if (!window.Object.is(parseInt(page), NaN) && page !== '...' && typeof page !== 'object') {
            goToPage(page)

            searchTop.scrollIntoView()
          }
        })
      }
    }
  })

  const paginationPrev = document.querySelector('.pagination__item--prev')
  // add event listener to pagination prev and next buttons
  paginationPrev.addEventListener('click', (event) => {
    event.preventDefault()
    const currentPage = store.meta('currentPage')

    paginationNavItems.forEach((paginationNumber) => {
      paginationNumber.classList.remove('pagination__item--current')

      if (paginationNumber.querySelector('.pagination__link').innerText === currentPage - 1) {
        paginationNumber.classList.add('pagination__item--current')
      }

      checkIfDisabled()
    })

    goToPage(currentPage - 1)
  })

  const paginationNext = document.querySelector('.pagination__item--next')
  paginationNext.addEventListener('click', (event) => {
    event.preventDefault()
    const currentPage = store.meta('currentPage')

    paginationNavItems.forEach((paginationNumber) => {
      paginationNumber.classList.remove('pagination__item--current')

      if (paginationNumber.querySelector('.pagination__link').innerText === currentPage + 1) {
        paginationNumber.classList.add('pagination__item--current')
      }

      checkIfDisabled()
    })

    goToPage(currentPage + 1)
  })
}

const initPagination = () => {
  // With comments removed, this is all that's left. Can it just be exported directly?
  initPaginationHandlers()
}

export { initPagination, buildPagination }
